import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GitCompareArrows, Plus, Minus, Equal, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ReportVersionDiffProps {
  versionA: { version_number: number; snapshot: any; created_at: string };
  versionB: { version_number: number; snapshot: any; created_at: string };
}

// Report sections we compare
const REPORT_SECTIONS = [
  { key: "executive_summary", label: "Executive Summary" },
  { key: "problem_and_context", label: "Problem & Kontext" },
  { key: "options_overview", label: "Optionen-Übersicht" },
  { key: "comparison_matrix_summary", label: "Bewertungsmatrix" },
  { key: "recommendation", label: "Empfehlung" },
  { key: "alternative_recommendation", label: "Alternative Empfehlung" },
  { key: "scenario_analysis", label: "Szenario-Analyse" },
  { key: "sensitivity_analysis", label: "Sensitivitätsanalyse" },
  { key: "confidence_intervals", label: "Konfidenzintervalle" },
  { key: "decision_checklist", label: "Entscheidungs-Checkliste" },
  { key: "financial_model", label: "Finanzmodell" },
  { key: "action_roadmap", label: "Aktionsplan" },
] as const;

type DiffStatus = "unchanged" | "modified" | "added" | "removed";

interface SectionDiff {
  key: string;
  label: string;
  status: DiffStatus;
  changedFields?: string[];
}

function deepCompare(a: any, b: any): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function getChangedFields(a: any, b: any, prefix = ""): string[] {
  if (!a || !b) return [];
  if (typeof a !== "object" || typeof b !== "object") return [];

  const changes: string[] = [];
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of allKeys) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (!(key in a)) {
      changes.push(`+ ${fullKey}`);
    } else if (!(key in b)) {
      changes.push(`- ${fullKey}`);
    } else if (!deepCompare(a[key], b[key])) {
      if (typeof a[key] === "object" && typeof b[key] === "object" && !Array.isArray(a[key])) {
        changes.push(...getChangedFields(a[key], b[key], fullKey));
      } else {
        changes.push(`~ ${fullKey}`);
      }
    }
  }
  return changes;
}

function computeDiff(snapshotA: any, snapshotB: any): SectionDiff[] {
  return REPORT_SECTIONS.map(({ key, label }) => {
    const a = snapshotA?.[key];
    const b = snapshotB?.[key];

    const aEmpty = !a || (typeof a === "object" && Object.keys(a).length === 0);
    const bEmpty = !b || (typeof b === "object" && Object.keys(b).length === 0);

    if (aEmpty && bEmpty) return { key, label, status: "unchanged" as DiffStatus };
    if (aEmpty && !bEmpty) return { key, label, status: "added" as DiffStatus };
    if (!aEmpty && bEmpty) return { key, label, status: "removed" as DiffStatus };

    if (deepCompare(a, b)) {
      return { key, label, status: "unchanged" as DiffStatus };
    }

    const changedFields = getChangedFields(a, b).slice(0, 8); // limit to 8
    return { key, label, status: "modified" as DiffStatus, changedFields };
  });
}

const statusConfig: Record<DiffStatus, { icon: typeof Equal; color: string; label: string }> = {
  unchanged: { icon: Equal, color: "text-muted-foreground", label: "Unverändert" },
  modified: { icon: ArrowRight, color: "text-amber-600 dark:text-amber-400", label: "Geändert" },
  added: { icon: Plus, color: "text-green-600 dark:text-green-400", label: "Neu" },
  removed: { icon: Minus, color: "text-red-600 dark:text-red-400", label: "Entfernt" },
};

export const ReportVersionDiff = ({ versionA, versionB }: ReportVersionDiffProps) => {
  const diffs = useMemo(
    () => computeDiff(versionA.snapshot, versionB.snapshot),
    [versionA.snapshot, versionB.snapshot]
  );

  const changedCount = diffs.filter((d) => d.status !== "unchanged").length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <GitCompareArrows className="h-5 w-5" />
            Versions-Vergleich
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">v{versionA.version_number}</Badge>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <Badge variant="outline">v{versionB.version_number}</Badge>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {changedCount === 0
            ? "Keine Änderungen zwischen den Versionen."
            : `${changedCount} von ${diffs.length} Sektionen geändert.`}
        </p>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {diffs.map((diff) => {
          const config = statusConfig[diff.status];
          const Icon = config.icon;

          return (
            <div
              key={diff.key}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                diff.status === "unchanged"
                  ? "bg-muted/30 border-border/30"
                  : diff.status === "modified"
                    ? "bg-amber-50/50 dark:bg-amber-950/10 border-amber-200/50 dark:border-amber-800/30"
                    : diff.status === "added"
                      ? "bg-green-50/50 dark:bg-green-950/10 border-green-200/50 dark:border-green-800/30"
                      : "bg-red-50/50 dark:bg-red-950/10 border-red-200/50 dark:border-red-800/30"
              )}
            >
              <Icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", config.color)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{diff.label}</span>
                  <Badge
                    variant="outline"
                    className={cn("text-[10px] px-1.5 py-0 h-4", config.color)}
                  >
                    {config.label}
                  </Badge>
                </div>
                {diff.changedFields && diff.changedFields.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {diff.changedFields.map((field) => (
                      <Badge
                        key={field}
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-4 font-mono text-muted-foreground border-border/50"
                      >
                        {field}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
